import {
  CreateVatRateInput,
  CreateVatRateMutationVariables,
  CustomSubscription,
  CustomSubscriptionType,
  DeleteVatRateMutationVariables,
  OnCreateCustomSubscriptionSubscription,
  OnDeleteCustomSubscriptionSubscription,
  OnUpdateCustomSubscriptionSubscription,
  UpdateVatRateInput,
  VAT_RATE_TABLE_NAME,
  VatRate,
  cleanInputCreate,
  cleanInputUpdate,
  createVatRate as createMutation,
  DateTime,
  deleteVatRate as deleteMutation,
  getReadId,
  getTableClientId,
  getTypedCustomSubscriptionValueForRefetch,
  onCreateCustomSubscription,
  onDeleteCustomSubscription,
  onUpdateCustomSubscription,
  syncVatRates,
  todayDateTime,
  updateVatRate as updateMutation,
  getDateTimeDifference,
} from '@rentguru/commons-utils';
import { NUMBER_OF_MINUTES_FOR_REFETCH, deleteAndHideEntity, list, mutation, useSubscriptions } from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import React, { Reducer, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';

interface VatRatesState {
  loading: boolean;
  error: string | null;
  shouldFetchVatRates: boolean;
  vatRates: VatRate[] | null;
  lastFetchForVatRate: DateTime | null;
}

export interface VatRatesContext extends VatRatesState {
  setFetchVatRates: () => void;
  getVatRate: (id: string | null | undefined) => VatRate | null | undefined;
  createVatRate: (input: Omit<CreateVatRateInput, 'clientId' | 'readId'>) => Promise<VatRate>;
  updateVatRate: (updates: UpdateVatRateInput) => Promise<VatRate>;
  deleteVatRate: (vatRate: VatRate) => Promise<VatRate | null>;
}

type Action =
  | {
      type: 'SHOULD_FETCH' | 'IS_FETCHING';
    }
  | {
      type: 'FETCH_FAILED';
    }
  | {
      type: 'FETCHED';

      payload: { vatRates: VatRate[] };
    }
  | {
      type: 'ADD_VAT_RATE' | 'UPDATE_VAT_RATE';

      payload: { vatRate: VatRate };
    }
  | {
      type: 'DELETE_VAT_RATE';

      payload: { id: string };
    };

const initialState: VatRatesState = {
  loading: false,
  error: null,
  shouldFetchVatRates: false,
  vatRates: null,
  lastFetchForVatRate: null,
};

const vatRatesReducer = (state: VatRatesState, action: Action): VatRatesState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      // Check //lastFetchForVatRate
      if (
        state.loading ||
        state.shouldFetchVatRates ||
        (state.lastFetchForVatRate &&
          getDateTimeDifference({ start: state.lastFetchForVatRate, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetchVatRates: true,
      };
    case 'IS_FETCHING':
      return {
        ...state,
        loading: true,
      };
    case 'FETCHED':
      return {
        ...state,
        loading: false,
        shouldFetchVatRates: false,
        vatRates: action.payload.vatRates,
        lastFetchForVatRate: todayDateTime(),
      };
    case 'FETCH_FAILED':
      return { ...state, loading: false, shouldFetchVatRates: false };
    case 'ADD_VAT_RATE':
      if (!state.vatRates) {
        return state;
      }
      return {
        ...state,
        vatRates: [...state.vatRates, action.payload.vatRate],
      };
    case 'UPDATE_VAT_RATE':
      if (!state.vatRates) {
        return state;
      }
      return {
        ...state,
        vatRates: state.vatRates.map((vo) => {
          if (vo.id === action.payload.vatRate.id) {
            return action.payload.vatRate;
          }
          return vo;
        }),
      };
    case 'DELETE_VAT_RATE':
      if (!state.vatRates) {
        return state;
      }
      return {
        ...state,
        vatRates: state.vatRates.filter((vo) => vo.id !== action.payload.id),
      };
    default:
      return state;
  }
};

export const VatRatesContext = React.createContext<VatRatesContext | null>(null);

export const VatRatesContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { clientId } = useUser();
  const { financialTransactionsDelete } = usePermissions();
  const [state, dispatch] = useReducer<Reducer<VatRatesState, Action>>(vatRatesReducer, initialState);

  const fetchVatRates = async (): Promise<VatRate[]> => {
    return await list<VatRate>(syncVatRates, 'clientId', getTableClientId(clientId!, 'VatRate'));
  };

  const setFetchVatRates = () => {
    dispatch({ type: 'SHOULD_FETCH' });
  };

  useEffect(() => {
    const fetchAndSet = async () => {
      dispatch({ type: 'IS_FETCHING' });
      try {
        const result = await fetchVatRates();
        dispatch({ type: 'FETCHED', payload: { vatRates: result } });
      } catch (error) {
        console.error('[vatRates] fetching vat rates failed', error);
        dispatch({ type: 'FETCH_FAILED' });
      }
    };
    if (state.shouldFetchVatRates) fetchAndSet();
    // eslint-disable-next-line
  }, [state.shouldFetchVatRates]);

  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const newData = data.onCreateCustomSubscription as CustomSubscription;
      if (
        newData.name === CustomSubscriptionType.REFETCH &&
        getTypedCustomSubscriptionValueForRefetch(newData).table === VAT_RATE_TABLE_NAME
      ) {
        setFetchVatRates();
      }
    },
    () => {},
    () => {}
  );

  const getVatRate = (id: string | null | undefined) => {
    if (isNil(id) || state.loading || state.error || !state.vatRates) {
      return null;
    }
    return state.vatRates.find((vo) => vo.id === id);
  };

  const createVatRate = async (input: Omit<CreateVatRateInput, 'clientId' | 'readId'>): Promise<VatRate> => {
    const result = await mutation<VatRate, CreateVatRateMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateVatRateInput),
        readId: getReadId(clientId!, 'VatRate'),
        clientId: getTableClientId(clientId!, 'VatRate'),
      },
    });
    dispatch({ type: 'ADD_VAT_RATE', payload: { vatRate: result } });
    dispatch({ type: 'SHOULD_FETCH' });
    return result;
  };

  const updateVatRate = async (updates: UpdateVatRateInput): Promise<VatRate> => {
    const result = await mutation<VatRate>(updateMutation, {
      input: cleanInputUpdate(updates, false),
    });
    dispatch({ type: 'UPDATE_VAT_RATE', payload: { vatRate: result } });
    dispatch({ type: 'SHOULD_FETCH' });
    return result;
  };

  const deleteVatRate = async (vatRate: VatRate): Promise<VatRate | null> => {
    if (!financialTransactionsDelete) {
      return null;
    }
    const result = await deleteAndHideEntity<VatRate, DeleteVatRateMutationVariables>(vatRate, deleteMutation);

    dispatch({ type: 'DELETE_VAT_RATE', payload: { id: vatRate.id } });
    return result;
  };

  const values = useMemo(
    () => ({
      ...state,
      setFetchVatRates,
      getVatRate,
      createVatRate,
      updateVatRate,
      deleteVatRate,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <VatRatesContext.Provider value={values}>{children}</VatRatesContext.Provider>;
};

export const useVatRates = (): VatRatesContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<VatRatesContext | null>(VatRatesContext);

  if (isNil(context)) {
    throw new Error('`useVatRates` hook must be used within a `VatRatesContext` component');
  }

  useEffect(() => {
    if (!firstRender.current) context.setFetchVatRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  return context;
};
